<template>
  <b-card>
    <b-alert v-if="err" show variant="danger">
      <p><pre>{{ err }}</pre></p>
    </b-alert>
    <template v-else-if="!company">
      <no-ssr><loading-spinner class="d-flex justify-content-center my-5"/></no-ssr>
    </template>
    <template v-else>
      <h3>Preferences</h3>

      <form-block v-for="(block, key) in blocks" :key="key" class="py-3"
        :title="block.title" :description="block.description" :company="company"
        :err="formErrs[key]" :state="formStates[key]"
        :fields="block.fields" :value="getValues(key)" @input="values => saveValues(key, values)"/>
    </template>
  </b-card>
</template>

<script>
import _get from 'lodash/get';
import gql from 'graphql-tag';
import enumsMixin from '@/mixins/enums';
import FormBlock from '@/components/settings/FormBlock';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import { createSingleSignOnButton } from '@src/lib/branding';

const CompanySettingsFragment = gql`
  fragment CompanySettings on Company {
    id

    name
    address
    country

    branding {
      smallLogo {
        id
      }
    }

    cognito {
      providers {
        type
        handle
        identifiers
      }
    }

    preferences {
      isContentShareToolEnabled
      isInternalMobilityEnabled
      isReferralsFeatureEnabled
      isMentorshipFeatureEnabled
      isCampaignsEnabled

      signInProviders
      canRegisterWithEmailDomains
      canRegisterWithoutInvite
      isShareLinkEnabled
      isNewDashboard
      isNewHeader

      defaultJobOrderBy
      defaultInternalJobOrderBy
      defaultRecruiterJobOrderBy
      isInterestBasedMatchingEnabled
      useManyLanguages
      supportedLanguages
      defaultLanguage
      canDecideDefaultLanguage

      canSyncContacts
      canSyncContactsWithLinkedIn
      canSyncContactsWithGoogle
      canSyncContactsWithMicrosoft
      boostContactCompanyNames
      ignoreContactCompanyNames
      ignoreContactEmailDomains

      jobShareText
      canShareJobsWith
      contentShareText
      canShareContentSharesWith

      recruiterApplicationEmail
      hideJobExcerpts
      departmentLabel
      askUserForDepartment
      askUserForLocation
      showAddJobsButton
      showCvUploadBox
      showApplicantsName
      matchIfContactLocationNearJobLocation
      jobReferralBonusDefault
      internalApplicationFormText
      externalLearningPlatformText
      showLinkedInSearchOnJobs
      canScheduleLinkedInPosts

      applyWithResume
      applyWithCoverLetter
      applyWithLocation
      canEmployeeApply
    }
  }
`;

const SettingsQuery = gql`
  ${CompanySettingsFragment}

  query SettingsPreferences {
    company {
      ...CompanySettings
    }
  }
`;

const SettingsMutation = gql`
  ${CompanySettingsFragment}

  mutation SettingsPreferences($update: CompanyUpdateInput!) {
    updateCompany(update: $update) {
      ...CompanySettings
    }
  }
`;

export default {
  name: 'CompanyPreferencesSettings',
  components: {
    FormBlock,
    LoadingSpinner,
  },
  mixins: [
    enumsMixin,
  ],
  data: () => ({
    company: null,
    err: null,
    formErrs: null,
    formStates: null,
  }),
  metaInfo: {
    title: 'Company Settings',
  },
  created() {
    this.formStates = Object.keys(this.blocks).reduce((states, key) => {
      states[key] = null;
      return states;
    }, {});
    this.formErrs = Object.keys(this.blocks).reduce((states, key) => {
      states[key] = null;
      return states;
    }, {});
  },
  apollo: {
    company: {
      query: SettingsQuery,
      results({ loading, data }) {
        if (!loading) {
          this.onFormReset();
        }
      },
      error(err) {
        this.err = `${err}`;
      },
    },
  },
  computed: {
    blocks() {
      const { preferences } = this.company || {};
      const { signInProviders } = preferences || {};

      return {
        customiseCompany: {
          title: 'Company Details',
          description: 'Customise company details',
          fields: [
            { label: 'Name', key: 'name', type: 'string' },
            { label: 'Address', key: 'address', type: 'multiline-string' },
            { label: 'Country', key: 'country', type: 'string' },
          ],
        },
        customisePlatform: {
          title: 'Platform',
          description: 'Customise various aspects & behaviours of the platform',
          fields: [
            {
              key: 'preferences.defaultJobOrderBy',
              label: 'Default Employee Job Sort',
              type: 'enum',
              options: this.jobOrderByEnums,
              empty: 'No preference',
            },
            {
              key: 'preferences.defaultRecruiterJobOrderBy',
              label: 'Default Recruiter Job Sort',
              type: 'enum',
              options: this.jobOrderByEnums,
              empty: 'No preference',
            },
            {
              key: 'preferences.defaultInternalJobOrderBy',
              label: 'Default Internal Job Sort',
              type: 'enum',
              options: this.internalJobOrderByEnums,
              empty: 'No preference',
            },
            {
              key: 'preferences.isInterestBasedMatchingEnabled',
              label: 'Enable interest based matching. If this is disabled a combined score will bbe used for relevancy.',
              type: 'boolean',
            },
            {
              label: 'Hide Job Excerpts',
              key: 'preferences.hideJobExcerpts',
              type: 'boolean',
            },
            {
              key: 'preferences.departmentLabel',
              label: '"Department" Label',
              description: 'You can optionally rename the "Department" noun across the platform - singular noun please',
              type: 'string',
            },
            {
              label: 'Recruiter Application Email',
              key: 'preferences.recruiterApplicationEmail',
              type: 'string',
            },
            {
              key: 'preferences.isContentShareToolEnabled',
              label: 'Enable content share tool',
              type: 'boolean',
            },
            {
              key: 'preferences.isInternalMobilityEnabled',
              label: 'Enable internal moblity feature',
              type: 'boolean',
            },
            {
              key: 'preferences.isReferralsFeatureEnabled',
              label: 'Enable referrals feature on the platform',
              type: 'boolean',
            },
            {
              key: 'preferences.isMentorshipFeatureEnabled',
              label: 'Enable mentorship feature on the platform',
              type: 'boolean',
            },
            {
              key: 'preferences.isCampaignsEnabled',
              label: 'Enable campaigns feature on the platform',
              type: 'boolean',
            },
            {
              key: 'preferences.isShareLinkEnabled',
              label: 'Enable Re-Share Links',
              description: 'Generate a link that prompts employees to share the job with their contacts & friends.',
              type: 'boolean',
            },
            {
              key: 'preferences.showAddJobsButton',
              label: 'Show "Add Job" button',
              description: 'At the top of Jobs page',
              type: 'boolean',
            },
            {
              key: 'preferences.isNewDashboard',
              label: 'Show New Dashboard',
              description: 'Display the new customisable dashboard layout',
              type: 'boolean',
            },
            {
              key: 'preferences.isNewHeader',
              label: 'Show New Header',
              description: 'Display the new fixed header (top menu bar)',
              type: 'boolean',
            },
            {
              key: 'preferences.showCvUploadBox',
              label: 'Show "Upload CV" on Job page',
              description: 'Allow employees to directly upload a CV',
              type: 'boolean',
            },
            {
              key: 'preferences.showApplicantsName',
              label: 'Show Applicant\'s Name',
              description: 'Optionally hide applicant names from Your Referrals page',
              type: 'boolean',
            },
            {
              label: 'Applications should have a resume',
              key: 'preferences.applyWithResume',
              description: 'Set the state of the Resume field when applying',
              type: 'enum',
              options: [ { text: 'Required', value: true }, { text: 'Optional', value: false }, { text: 'Hide', value: null } ],
            },
            {
              label: 'Applications should have a location field',
              key: 'preferences.applyWithLocation',
              description: 'Set the state of the Location field when applying',
              type: 'enum',
              options: [ { text: 'Required', value: true }, { text: 'Optional', value: false }, { text: 'Hide', value: null } ],
            },
            {
              label: 'Applications should have a cover letter',
              key: 'preferences.applyWithCoverLetter',
              description: 'Set the state of the Resume field when applying',
              type: 'enum',
              options: [ { text: 'Required', value: true }, { text: 'Optional', value: false }, { text: 'Hide', value: null } ],
            },
            {
              key: 'preferences.matchIfContactLocationNearJobLocation',
              label: 'Only match contacts if they are near the job location',
              description: 'Optionally restrict matches to nearby the job location',
              type: 'boolean',
            },
            {
              label: 'Job Default Referral Bonus',
              key: 'preferences.jobReferralBonusDefault',
              description: 'Freeform text to show if no specific "Referral Bonus" is set on a job',
              type: 'string',
            },
            {
              label: 'Text on Internal Job Application Form',
              key: 'preferences.internalApplicationFormText',
              description: 'Text to show on internal application form',
              type: 'textarea',
            },
            {
              label: 'External learning options description',
              key: 'preferences.externalLearningPlatformText',
              description: 'This is a short text that will be displayed on the \'Get help to prepare\' section of internal jobs',
              type: 'textarea',
            },
            {
              key: 'preferences.showLinkedInSearchOnJobs',
              label: 'Show LinkedIn search popup on jobs',
              type: 'boolean',
            },
            {
              key: 'preferences.canScheduleLinkedInPosts',
              label: 'Can schedule posts on LinkedIn',
              type: 'boolean',
            },
            {
              key: 'preferences.canEmployeeApply',
              label: 'Can employees apply for vacancies too?',
              type: 'boolean',
            },
          ],
        },
        // customiseLanguage: {
        //   title: 'Languages',
        //   description: 'Customise various languages & localisations',
        //   fields: [
        //     {
        //       key: 'preferences.useManyLanguages',
        //       label: 'Use Many Languages',
        //       type: 'boolean',
        //     },
        //     {
        //       key: 'preferences.supportedLanguages',
        //       label: 'Supported Languages',
        //       type: 'string[]',
        //     },
        //     {
        //       key: 'preferences.defaultLanguage',
        //       label: 'Default Language',
        //       type: 'string',
        //     },
        //     {
        //       key: 'preferences.canDecideDefaultLanguage',
        //       label: 'Users can decide their default language',
        //       type: 'boolean',
        //     },
        //   ],
        // },
        customiseUsers: {
          title: 'Users, Sign-In & Sign-Up',
          description: 'Customise user actions, including sign-in & sign-up',
          fields: [
            {
              key: 'preferences.signInProviders',
              label: 'Users can sign-in using',
              disabled: !(this.company && this.company.cognito && Array.isArray(this.company.cognito.providers)),
              options: this.company && this.company.cognito && Array.isArray(this.company.cognito.providers)
                ? this.company.cognito.providers.map(({ type, handle, identifiers }) => {
                  if (type === 'COGNITO') {
                    return { text: 'Email/Password', value: 'email-password' };
                  } else {
                    const { name } = createSingleSignOnButton(type, handle);
                    const text = name || `${type} ${handle}`;
                    const domains = Array.isArray(identifiers) ? identifiers.join(', ') : '';
                    return { text: `${text}${domains ? ` (${domains})` : ''}`, value: handle };
                  }
                })
                : [],
              type: 'ordered-checkbox-group',
            },
            {
              key: 'preferences.canRegisterWithEmailDomains',
              label: 'With email/password, users can register with these email domains',
              disabled: !(Array.isArray(signInProviders) && signInProviders.includes('email-password')),
              placeholder: 'Add domain..',
              type: 'string[]',
            },
            {
              key: 'preferences.canRegisterWithoutInvite',
              label: 'Users can register without an invitation',
              type: 'boolean',
            },
            {
              key: 'preferences.askUserForDepartment',
              label: 'Ask users to select their department on sign-up',
              type: 'boolean',
            },
            {
              key: 'preferences.askUserForLocation',
              label: 'Ask users to select their location on sign-up',
              type: 'boolean',
            },
          ],
        },
        // customiseContactSync: {
        //   title: 'Contact Syncing',
        //   description: 'Customise which networks can be used for contact-syncing, and other settings',
        //   fields: [
        //     {
        //       key: 'preferences.canSyncContacts',
        //       label: 'Users can sync contacts to the platform',
        //       type: 'boolean',
        //     },
        //     {
        //       key: 'preferences.canSyncContactsWithLinkedIn',
        //       label: 'Allow contacts to sync from LinkedIn',
        //       type: 'boolean',
        //     },
        //     {
        //       key: 'preferences.canSyncContactsWithGoogle',
        //       label: 'Allow contacts to sync from Google',
        //       type: 'boolean',
        //     },
        //     {
        //       key: 'preferences.canSyncContactsWithMicrosoft',
        //       label: 'Allow contacts to sync from Microsoft',
        //       type: 'boolean',
        //     },
        //     {
        //       key: 'preferences.boostContactCompanyNames',
        //       label: 'Boost contacts from these companies',
        //       type: 'string[]',
        //     },
        //     {
        //       key: 'preferences.ignoreContactCompanyNames',
        //       label: 'Ignore contacts from these companies',
        //       type: 'string[]',
        //     },
        //     {
        //       key: 'preferences.ignoreContactEmailDomains',
        //       label: 'Ignore contacts from these email domains',
        //       type: 'string[]',
        //     },
        //   ],
        // },
        customiseSharing: {
          title: 'Sharing options',
          description: 'Edit default share text & enable/disable individual share buttons',
          icon: 'share',
          fields: [
            {
              key: 'preferences.canShareJobsWith',
              label: 'Job Share Providers',
              description: 'Customise the share providers shown for jobs',
              type: 'ordered-checkbox-group',
              options: [
                { text: 'Email', value: 'email' },
                { text: 'LinkedIn', value: 'linkedin' },
                { text: 'WhatsApp', value: 'whatsapp' },
                { text: 'Facebook', value: 'facebook' },
                { text: 'Facebook Messenger', value: 'fb-messenger' },
                { text: 'Twitter', value: 'twitter' },
                { text: 'Xing', value: 'xing' },
              ],
            },
            {
              key: 'preferences.jobShareText',
              label: 'Job Share Text',
              placeholder: 'Are you interested in working with me as a {jobTitle}?',
              type: 'string',
              tokens: {
                jobTitle: 'The job title',
              },
            },
            {
              key: 'preferences.canShareContentSharesWith',
              label: 'Content Share Providers',
              description: 'Customise the share providers shown for content',
              type: 'ordered-checkbox-group',
              options: [
                { text: 'Email', value: 'email' },
                { text: 'LinkedIn', value: 'linkedin' },
                { text: 'WhatsApp', value: 'whatsapp' },
                { text: 'Facebook', value: 'facebook' },
                { text: 'Facebook Messenger', value: 'fb-messenger' },
                { text: 'Twitter', value: 'twitter' },
                { text: 'Xing', value: 'xing' },
              ],
            },
            {
              key: 'preferences.contentShareText',
              label: 'Content Share Text',
              placeholder: '{contentTitle}',
              type: 'string',
              tokens: {
                title: 'The content title',
              },
            },
          ],
        },
      };
    },
  },
  methods: {
    getValues(key) {
      return this.blocks[key].fields.reduce((values, field) => {
        values[field.key] = _get(this.company, field.key, null);
        return values;
      }, {});
    },
    async saveValues(key, values) {
      try {
        const block = this.blocks[key];
        if (!block) {
          throw new Error(`Unknown block ${key}`);
        }

        this.formStates[key] = 'SAVING';
        this.formErrs[key] = null;

        const update = block.fields.reduce((update, field) => {
          if (field.key.indexOf('branding.') === 0) {
            update.branding = update.branding || {};
            update.branding[field.key.replace('branding.', '')] = values[field.key];
          } else if (field.key.indexOf('preferences.') === 0) {
            update.preferences = update.preferences || {};
            update.preferences[field.key.replace('preferences.', '')] = values[field.key];
          } else if (field.key.indexOf('template.') === 0) {
            const { key, lang, value } = values[field.key];
            update.templates = update.templates || [];
            update.templates.push({ key, lang, value });
          } else {
            update[field.key] = values[field.key];
          }
          return update;
        }, {});

        console.log(block, update);

        await this.$apollo.mutate({
          mutation: SettingsMutation,
          variables: { update },
          update(store, { data: { updateCompany: company } }) {
            const data = store.readQuery({ query: SettingsQuery });
            data.company = company;
            store.writeQuery({ query: SettingsQuery, data });
          },
        });

        this.formStates[key] = 'SAVED';
      } catch (err) {
        this.formStates[key] = 'ERRORED';
        this.formErrs[key] = `${err}`;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/settings/Preferences.vue